import { client } from './client';
import type { Order, OrderItem, OrderMeasurement, OrderRemark } from '../types';

export const api = {
  get: (url: string, config?: any): Promise<any> => client.get(url, config),
  post: (url: string, data?: any, config?: any): Promise<any> => client.post(url, data, config),
  put: (url: string, data?: any, config?: any): Promise<any> => client.put(url, data, config),
  delete: (url: string, config?: any): Promise<any> => client.delete(url, config),
};

export default api;

// 导出client供外部使用
export { client };

// 【新增】创建订单请求类型
export interface OrderCreateRequest {
  customer_id: number;
  customer_name?: string;
  customer_gender?: number;
  customer_email?: string;
  customer_phone?: string;
  height?: number;
  height_unit?: 'CM' | 'FT';
  weight?: number;
  weight_unit?: 'KG' | 'LB';
  delivery_date?: string;
  delivery_days?: number;
  shipping_method: string;
  shipping_address?: string;
  shipping_zip?: string;
  trial_fitting?: string;
  is_urgent?: number;
  copy_from_order_id?: number;
  items: OrderItem[];
  measurements?: OrderMeasurement;
  remarks?: OrderRemark[];
}

// 【新增】更新订单请求类型
export interface OrderUpdateRequest extends Partial<Order> {
  adjust_reason?: string;
}

// 【新增】报价请求
export interface OrderQuoteRequest {
  items: OrderItem[];
  shipping_method?: string;
  special_design_amount?: number;
  extra_charges?: any[];
  fabric_type?: string;
  fabric_code?: string;
  fabric_id?: number;
  shipping_amount?: number;
  tariff_amount?: number;
  quote_mode?: string;
  exchange_rate?: number;
}

// 认证相关
export const authApi = {
  login: (data: { username: string; password: string }) => client.post('/auth/login', data),
  me: () => client.get('/auth/me'),
  refresh: () => client.post('/auth/refresh'),
  register: (data: { store_name: string; contact_name?: string; phone?: string; address?: string; salesperson_id?: number | null }) =>
    client.post('/auth/register', data),
};

// 订单相关
export const orderApi = {
  list: (params?: any) => client.get('/orders', { params }),
  get: (id: number) => client.get(`/orders/${id}`),
  detail: (id: number) => client.get(`/orders/${id}`),
  // 「试身尺寸」：取本单复制来源的那张试身单（试身/手工试毛样）的审核成衣尺寸，
  // 仅对试身要求为「试身回」的订单有意义；后端会校验客户名与近3个月内
  trialMeasurement: (params: { source_order_id: number; customer_name: string }) =>
    client.get('/orders/trial-measurement', { params }),
  create: (data: OrderCreateRequest) => client.post('/orders', data),
  update: (id: number, data: any) => client.put(`/orders/${id}`, data),
  updateStatus: (id: number, statusOrData: any, remark?: string) => {
    const data = typeof statusOrData === 'object' && statusOrData !== null ? statusOrData : { status: statusOrData, remark };
    return client.put(`/orders/${id}/status`, data);
  },
  updateFabricQty: (id: number, fabric_qty: number) => client.put(`/orders/${id}/fabric-qty`, { fabric_qty }),
  quote: (data: OrderQuoteRequest) => client.post('/orders/quote', data),
  delete: (id: number) => (client as any).delete(`/orders/${id}`),
  // 订单快照/修改记录
  snapshots: (id: number) => client.get(`/orders/${id}/snapshots`),
  // 订单相关附加功能
  createDraft: (data: OrderCreateRequest) => client.post('/orders/draft', data),
  draft: (data: OrderCreateRequest) => client.post('/orders/draft', data),
  recalculatePrice: (id: number) => client.post(`/orders/${id}/price`),
  generateInvoice: (id: number, data: any) => client.post(`/orders/${id}/invoice`, data),
  // 售后相关
  getAfterSales: (id: number) => client.get(`/orders/${id}/after-sales`),
  createAfterSales: (id: number, data: any) => client.post(`/orders/${id}/after-sales`, data),
  // 【新增】复制订单
  copy: (id: number) => client.post(`/orders/${id}/copy`),
  // 批量复制专用：带 batch 开关。后端据此①收紧订单归属校验 ②新单不留与原单的关联
  // （copy_from_order_id 仅试身单保留、HP 特体里的工厂单号清空）。
  // 不带 batch 的 copy() 行为完全不变，门店端「复制订单」不受影响。
  batchCopy: (id: number) => client.post(`/orders/${id}/copy`, { batch: true }),
  // 【新增】生成 AI 渲染图
  generateRender: (id: number) => client.post(`/orders/${id}/render`),
  aiRender: (data: { order_id: number }) => client.post('/ai-render', data),
  aiRenderDescription: (data: { order_id: number }) => client.post('/ai-render/description', data),
  // 【新增】B端确认签收
  confirmReceipt: (id: number) => client.post(`/orders/${id}/confirm-receipt`),
  // 订单相关子资源（参考 B 端数据获取方式）
  payments: (id: number) => client.get(`/orders/${id}/payments`),
  invoices: (id: number) => client.get(`/orders/${id}/invoices`),
  approvalRemarks: (id: number) => client.get(`/orders/${id}/approval-remarks`),
  inventoryLogs: (id: number) => client.get(`/orders/${id}/inventory-logs`),
  operationLogs: (id: number) => client.get(`/orders/${id}/operation-logs`),
  // 全局操作日志（仅 admin/ops）
  allOperationLogs: (params?: any) => client.get('/operation-logs', { params }),
  // 上传体型照片
  uploadBodyPhoto: (data: FormData, no?: number) => client.post(`/orders/upload-body-photo?no=${no || ''}`, data),
  uploadImage: (data: FormData, folder?: string) => client.post(`/upload/image?folder=${folder || 'embroidery-images'}`, data),
  uploadRemarkImage: (data: FormData, remarkIndex: number, imageIndex: number, globalIndex?: number) => client.post(`/upload/remark-image?remark_index=${remarkIndex}&image_index=${imageIndex}${globalIndex != null ? `&global_index=${globalIndex}` : ''}`, data),
  // 【新增】保存 AI 建议
  saveAiSuggestion: (id: number, data: { suggestion: { status: string; description: string } }) => client.post(`/orders/${id}/ai-suggestion`, data),
  // 【新增】AI 尺码合规性校验
  aiCheck: (id: number) => client.post(`/orders/${id}/ai-check`),
  // 耗料计算：调用博亚 Calculatefabric 接口
  fabricCalculate: (id: number, fabric_type?: string) => client.post(`/orders/${id}/calculate-fabric`, { fabric_type: fabric_type || '净色' }),
  // 导出订单 PDF / Excel
  exportPdf: (id: number) => client.get(`/orders/${id}/export-pdf`, { responseType: 'blob' }),
  exportExcel: (id: number) => client.get(`/orders/${id}/export-excel`, { responseType: 'blob' }),
  updateRemarkVisibility: (id: number, visible: boolean | number) => client.put(`/orders/${id}/remark-visibility`, { visible_to_customer: visible }),
};

// 客户管理
export const customerApi = {
  list: (params?: any) => client.get('/customers', { params }),
  create: (data: any) => client.post('/customers', data),
  get: (id: number) => client.get(`/customers/${id}`),
  detail: (id: number) => client.get(`/customers/${id}`),
  update: (id: number, data: any) => client.put(`/customers/${id}`, data),
  delete: (id: number) => (client as any).delete(`/customers/${id}`),
  // 量体档案
  getMeasurements: (id: number) => client.get(`/customers/${id}/measurements`),
  measurements: (id: number) => client.get(`/customers/${id}/measurements`),
  createMeasurement: (id: number, data: any) => client.post(`/customers/${id}/measurements`, data),
  updateMeasurement: (customerId: number, measurementId: number, data: any) =>
    client.put(`/customers/${customerId}/measurements/${measurementId}`, data),
  deleteMeasurement: (customerId: number, measurementId: number) =>
    (client as any).delete(`/customers/${customerId}/measurements/${measurementId}`),
};

// 品类管理
export const categoryApi = {
  list: () => client.get('/categories'),
  create: (data: any) => client.post('/categories', data),
  update: (id: number, data: any) => client.put(`/categories/${id}`, data),
  delete: (id: number) => (client as any).delete(`/categories/${id}`),
};

// 设计项管理
export const designItemApi = {
  list: (params?: any) => client.get('/design-items', { params }),
  create: (data: any) => client.post('/design-items', data),
  update: (id: number, data: any) => client.put(`/design-items/${id}`, data),
  delete: (id: number) => (client as any).delete(`/design-items/${id}`),
};

// 工艺管理
export const craftApi = {
  list: () => client.get('/crafts'),
  create: (data: any) => client.post('/crafts', data),
  update: (id: number, data: any) => client.put(`/crafts/${id}`, data),
  delete: (id: number) => (client as any).delete(`/crafts/${id}`),
};

// 个性收费项管理
export const extraChargeApi = {
  list: () => client.get('/extra-charges'),
  create: (data: any) => client.post('/extra-charges', data),
  update: (id: number, data: any) => client.put(`/extra-charges/${id}`, data),
  delete: (id: number) => (client as any).delete(`/extra-charges/${id}`),
};

// 面料管理
export const fabricApi = {
  list: (params?: any) => client.get('/fabrics', { params }),
  create: (data: any) => client.post('/fabrics', data),
  update: (id: number, data: any) => client.put(`/fabrics/${id}`, data),
  delete: (id: number) => (client as any).delete(`/fabrics/${id}`),
};

// 其他物料管理
export const materialApi = {
  list: (params?: any) => client.get('/materials', { params }),
  linings: (params?: any) => client.get('/materials', { params: { type: 'lining', ...params } }),
  create: (data: any) => client.post('/materials', data),
  update: (id: number, data: any) => client.put(`/materials/${id}`, data),
  delete: (id: number) => (client as any).delete(`/materials/${id}`),
};

// 门店管理
export const storeApi = {
  list: (params?: any) => client.get('/stores', { params }),
  recent: (params?: any) => client.get('/stores/recent', { params }),
  get: (id: number) => client.get(`/stores/${id}`),
  create: (data: any) => client.post('/stores', data),
  update: (id: number, data: any) => client.put(`/stores/${id}`, data),
  delete: (id: number) => (client as any).delete(`/stores/${id}`),
  // 门店用户
  getUsers: (id: number) => client.get(`/stores/${id}/users`),
  bindUser: (id: number, data: any) => client.post(`/stores/${id}/users`, data),
  // 门店账单
  getBills: (id: number, params?: any) => client.get(`/stores/${id}/bills`, { params }),
  // 门店Dashboard
  getDashboard: (id: number) => client.get(`/stores/${id}/dashboard`),
};

// 采购管理
export const purchaseApi = {
  list: (params?: any) => client.get('/purchase-orders', { params }),
  get: (id: number) => client.get(`/purchase-orders/${id}`),
  create: (data: any) => client.post('/purchase-orders', data),
  update: (id: number, data: any) => client.put(`/purchase-orders/${id}`, data),
  delete: (id: number) => (client as any).delete(`/purchase-orders/${id}`),
  send: (id: number) => client.post(`/purchase-orders/${id}/send`),
  confirm: (id: number, data: any) => client.post(`/purchase-orders/${id}/confirm`, data),
};

// 库存管理
export const inventoryApi = {
  list: (params?: any) => client.get('/inventory', { params }),
  adjust: (data: any) => client.post('/inventory/adjust', data),
  alerts: () => client.get('/inventory/alerts'),
};

// 订单出入库
export const orderInventoryApi = {
  pending: (params?: any) => client.get('/order-inventory-logs/pending', { params }),
  batchMark: (data: any) => client.post('/order-inventory-logs/batch-mark', data),
  batchRevoke: (data: any) => client.post('/order-inventory-logs/batch-revoke', data),
  ocrExtract: (data: FormData, kind: 'tracking' | 'factory', strategy?: number) =>
    client.post(`/order-inventory-logs/ocr-extract?kind=${kind}${strategy ? `&strategy=${strategy}` : ''}`, data),
  matchCandidates: (candidates: string[]) =>
    client.post('/order-inventory-logs/match-candidates', { candidates }),
};

// 财务管理
export const financeApi = {
  // 付款
  payments: (params?: any) => client.get('/payments', { params }),
  createPayment: (data: any) => client.post('/payments', data),
  confirmPayment: (id: number, data?: any) => client.post(`/payments/${id}/confirm`, data),
  cancelPayment: (id: number, data?: any) => client.post(`/payments/${id}/cancel`, data),
  // 发票
  invoices: (params?: any) => client.get('/invoices', { params }),
  createInvoice: (data: any) => client.post('/invoices', data),
  getInvoice: (id: number) => client.get(`/invoices/${id}`),
  updateInvoice: (id: number, data: any) => client.put(`/invoices/${id}`, data),
  exportInvoice: (id: number, format: string, lang?: string) => client.get(`/invoices/${id}/export?format=${format}${lang ? `&lang=${lang}` : ''}`),
  batchPayment: (invoiceId: number, data: any) => client.post(`/invoices/${invoiceId}/batch-payment`, data),
  linkOrder: (invoiceId: number, data: any) => client.post(`/invoices/${invoiceId}/link-order`, data),
  // 从发票中移除已关联订单（「关联订单」的逆向操作，仅限未付款发票）
  unlinkOrder: (invoiceId: number, orderId: number) => client.post(`/invoices/${invoiceId}/unlink-order`, { order_id: orderId }),
  linkMisc: (invoiceId: number, data: any) => client.post(`/invoices/${invoiceId}/link-misc`, data),
  linkableOrders: (invoiceId: number, q?: string) => client.get(`/invoices/${invoiceId}/linkable-orders${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  getInvoicePaymentRecords: (invoiceId: number) => client.get(`/invoices/${invoiceId}/payment-records`),
  // 门店账单
  storeBills: (params?: any) => client.get('/finance/store-bills', { params }),
  // 工资
  salaries: (params?: any) => client.get('/finance/salaries', { params }),
  saveSalary: (data: any) => client.post('/finance/salaries', data),
  calcSalary: (data: any) => client.post('/finance/salary-calc', data),
  // 提成
  commissions: (params?: any) => client.get('/finance/sales-commissions', { params }),
  saveCommission: (data: any) => client.post('/finance/sales-commissions', data),
  // 汇率
  exchangeRates: () => client.get('/finance/exchange-rates'),
  saveExchangeRate: (data: any) => client.post('/finance/exchange-rates', data),
  currentExchangeRate: () => client.get('/finance/exchange-rate'),
  // 工厂成本
  factoryCosts: (params?: any) => client.get('/finance/factory-costs', { params }),
  saveFactoryCost: (data: any) => client.post('/finance/factory-costs', data),
  // 年终分红
  yearEndBonusList: (params?: any) => client.get('/finance/year-end-bonus', { params }),
  saveYearEndBonus: (data: any) => client.post('/finance/year-end-bonus', data),
  // 工作汇总（提成+工资条）
  payrollSummary: (month: string) => client.get('/payroll/summary', { params: { month } }),
  payrollUser: (userId: number, month: string) => client.get(`/payroll/user/${userId}`, { params: { month } }),
  savePayslip: (userId: number, data: any) => client.put(`/payroll/user/${userId}`, data),
  resetPayslip: (userId: number, month: string) => client.delete(`/payroll/user/${userId}`, { params: { month } }),
  payrollBatchGenerate: (month: string) => client.post('/payroll/batch-generate', { month }),
  payrollDistribution: (month: string) => client.get('/payroll/distribution', { params: { month } }),
  confirmPayrollDistribution: (month: string) => client.post('/payroll/distribution', { month }),
  // 取消发放（仅系统管理员）：解除该月冻结，恢复可编辑
  cancelPayrollDistribution: (month: string) => client.delete('/payroll/distribution', { params: { month } }),
  myPayslip: (month: string, params?: any) => client.get('/payroll/my-payslip', { params: { month, ...(params || {}) } }),
  confirmPayslip: (month: string) => client.post('/payroll/my-payslip', { month }),
  // 提成划转（请假/代管）
  commissionShares: (params?: any) => client.get('/commission-shares', { params }),
  createCommissionShare: (data: any) => client.post('/commission-shares', data),
  updateCommissionShare: (id: number, data: any) => client.put(`/commission-shares/${id}`, data),
  deleteCommissionShare: (id: number) => (client as any).delete(`/commission-shares/${id}`),
};

// 加工商(工厂)工费对账（2026-10-22 改版：订单级列表 + 账单编号模型，金额CNY）
export const factoryReconApi = {
  // 订单级列表：账单编号/订单号或客户名/工厂单号或工厂供应商/门店编号/面料编号或面料供应商 + 对账/付款状态
  listOrders: (params?: any) => client.get('/factory-reconciliations/orders', { params }),
  // 查看弹窗详情：业务员/下单日期/完成日期/发票总额/待付金额
  orderDetail: (orderId: number) => client.get(`/factory-reconciliations/order/${orderId}`),
  // 工费金额(CNY)行内编辑
  updateFactoryCost: (orderId: number, factory_cost_cny: number | null) =>
    (client as any).put(`/factory-reconciliations/order/${orderId}/cost`, { factory_cost_cny }),
  // 单条记录：对账状态 未对账<->已对账 切换/回滚；未对账->已对账时需传 bill_no
  toggleRecon: (orderId: number, bill_no?: string) =>
    client.post(`/factory-reconciliations/order/${orderId}/toggle-recon`, { bill_no }),
  // 批量回滚未对账（同时清空账单编号与付款状态）
  batchRollback: (order_ids: number[]) =>
    client.post('/factory-reconciliations/batch-mark', { order_ids, recon_status: 0 }),
  // 批量对账：指定账单编号(NO+8位日期)，写入选中订单（必须同一加工商）
  batchRecon: (data: { order_ids: number[]; bill_no: string; remark?: string }) =>
    client.post('/factory-reconciliations/batch-recon', data),
  // 查看某账单下所有订单（供批量付款弹窗勾选）+ 付款记录
  billDetail: (billNo: string) => client.get(`/factory-reconciliations/bill/${encodeURIComponent(billNo)}`),
  // 账单付款：可勾选订单子集核销，支持多次付款，金额/截图/备注均非必填
  billPayments: (billNo: string, data: any) =>
    client.post(`/factory-reconciliations/bill/${encodeURIComponent(billNo)}/payments`, data),
};

export const employeeProfileApi = {
  list: () => client.get('/employee-profiles'),
  get: (userId: number) => client.get(`/employee-profiles/${userId}`),
  update: (userId: number, data: any) => client.put(`/employee-profiles/${userId}`, data),
  upload: (file: FormData) => client.post('/employee-profiles/upload', file, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (userId: number) => client.delete(`/employee-profiles/${userId}`),
};

// 价格体系
export const priceApi = {
  // 销售价格矩阵
  matrix: (params?: any) => client.get('/sales-prices/matrix', { params }),
  // 价格等级
  tiers: () => client.get('/price-tiers'),
  createTier: (data: any) => client.post('/price-tiers', data),
  updateTier: (id: number, data: any) => client.put(`/price-tiers/${id}`, data),
  deleteTier: (id: number) => (client as any).delete(`/price-tiers/${id}`),
  // 批量折扣
  discounts: () => client.get('/bulk-discounts'),
  createDiscount: (data: any) => client.post('/bulk-discounts', data),
  updateDiscount: (id: number, data: any) => client.put(`/bulk-discounts/${id}`, data),
  deleteDiscount: (id: number) => (client as any).delete(`/bulk-discounts/${id}`),
  // 工艺加工费
  craftFees: () => client.get('/craft-fees'),
  createCraftFee: (data: any) => client.post('/craft-fees', data),
  updateCraftFee: (id: number, data: any) => client.put(`/craft-fees/${id}`, data),
  deleteCraftFee: (id: number) => (client as any).delete(`/craft-fees/${id}`),
  // 毛利率
  margins: () => client.get('/gross-margins'),
  createMargin: (data: any) => client.post('/gross-margins', data),
  updateMargin: (id: number, data: any) => client.put(`/gross-margins/${id}`, data),
  deleteMargin: (id: number) => (client as any).delete(`/gross-margins/${id}`),
};

// 报表
export const reportApi = {
  sales: (params?: any) => client.get('/reports/sales', { params }),
  orders: (params?: any) => client.get('/reports/orders', { params }),
  costs: (params?: any) => client.get('/reports/costs', { params }),
  profit: (params?: any) => client.get('/reports/profit', { params }),
};

// 系统设置
export const settingApi = {
  list: (params?: any) => client.get('/system/settings', { params }),
  get: (key: string) => client.get(`/system/settings/${key}`),
  update: (key: string, data: any) => client.put(`/system/settings/${key}`, data),
};

// 角色管理
export const roleApi = {
  list: () => client.get('/roles'),
  create: (data: any) => client.post('/roles', data),
  update: (id: number, data: any) => client.put(`/roles/${id}`, data),
  delete: (id: number) => (client as any).delete(`/roles/${id}`),
};

// 用户管理
export const userApi = {
  list: (params?: any) => client.get('/users', { params }),
  create: (data: any) => client.post('/users', data),
  update: (id: number, data: any) => client.put(`/users/${id}`, data),
  delete: (id: number) => (client as any).delete(`/users/${id}`),
};

// 消息通知
export const notificationApi = {
  list: (params?: any) => client.get('/notifications', { params }),
  create: (data: any) => client.post('/notifications', data),
  read: (id: number) => client.post(`/notifications/${id}/read`),
  readAll: () => client.post('/notifications/read-all'),
  unreadCount: () => client.get('/notifications/unread-count'),
};

// 仓储物流
export const warehouseApi = {
  // 收货
  receiving: (params?: any) => client.get('/warehouse/receiving', { params }),
  createReceiving: (data: any) => client.post('/warehouse/receiving', data),
  // 质检
  qc: (params?: any) => client.get('/warehouse/qc', { params }),
  createQC: (data: any) => client.post('/warehouse/qc', data),
  // 发货
  shipping: (params?: any) => client.get('/warehouse/shipping', { params }),
  createShipping: (data: any) => client.post('/warehouse/shipping', data),
  syncShipping: (id: number) => client.post(`/warehouse/shipping/${id}/sync`),
};

// 供应商管理
export const supplierApi = {
  list: (params?: any) => client.get('/suppliers', { params }),
  create: (data: any) => client.post('/suppliers', data),
  update: (id: number, data: any) => client.put(`/suppliers/${id}`, data),
  delete: (id: number) => (client as any).delete(`/suppliers/${id}`),
};

// 工厂管理
export const factoryApi = {
  list: (params?: any) => client.get('/factories', { params }),
  create: (data: any) => client.post('/factories', data),
  update: (id: number, data: any) => client.put(`/factories/${id}`, data),
  delete: (id: number) => (client as any).delete(`/factories/${id}`),
  // 订单工厂交互
  sync: (orderId: number, data: any) => client.post(`/orders/${orderId}/factory-info`, data),
  syncStatus: (orderId: number) => client.post(`/orders/${orderId}/sync-status`),
};

// 工厂工艺选项映射管理
export const factoryCraftMappingApi = {
  list: (params?: any) => client.get('/factory-craft-mappings', { params }),
  create: (data: any) => client.post('/factory-craft-mappings', data),
  update: (id: number, data: any) => client.put(`/factory-craft-mappings/${id}`, data),
  delete: (id: number) => (client as any).delete(`/factory-craft-mappings/${id}`),
};

// 工厂推送日志（每次推送到工厂的结果与发现的问题）
export const factoryPushLogApi = {
  list: (params?: any) => client.get('/factory-push-logs', { params }),
  detail: (id: number) => client.get(`/factory-push-logs/${id}`),
};

// 工厂字段映射管理（姓名/电话/快递等基础字段）
export const factoryFieldMappingApi = {
  list: (params?: any) => client.get('/factory-field-mappings', { params }),
  create: (data: any) => client.post('/factory-field-mappings', data),
  update: (id: number, data: any) => client.put(`/factory-field-mappings/${id}`, data),
  delete: (id: number) => (client as any).delete(`/factory-field-mappings/${id}`),
};

// 工厂面料映射管理
export const factoryFabricMappingApi = {
  list: (params?: any) => client.get('/factory-fabric-mappings', { params }),
  create: (data: any) => client.post('/factory-fabric-mappings', data),
  update: (id: number, data: any) => client.put(`/factory-fabric-mappings/${id}`, data),
  delete: (id: number) => (client as any).delete(`/factory-fabric-mappings/${id}`),
};

// 报价单
export const quoteApi = {
  calculate: (data: any) => client.post('/quote/calculate', data),
  save: (data: any) => client.post('/quote/save', data),
  getSession: (sessionKey: string) => client.get(`/quote/session/${sessionKey}`),
  listSessions: (params?: any) => client.get('/quote/sessions', { params }),
  deleteSession: (sessionKey: string) => (client as any).delete(`/quote/session/${sessionKey}`),
  share: (sessionKey: string, data?: any) => client.post(`/quote/session/${sessionKey}/share`, data),
  // 导出接口耗时较长（记录多时可能需数十秒），单独放宽超时到 5 分钟，避免全局 30s 超时误报失败
  exportPdf: (sessionKey: string, params?: any) => client.get(`/quote/session/${sessionKey}/export/pdf`, { params, responseType: 'blob', timeout: 300000 }),
  exportExcel: (sessionKey: string, params?: any) => client.get(`/quote/session/${sessionKey}/export/excel`, { params, responseType: 'blob', timeout: 300000 }),
  unitPrice: (data: any) => client.post('/quote/unit-price', data),
  bulkDiscounts: () => client.get('/quote/bulk-discounts'),
};

// 报价草稿
export const quoteDraftApi = {
  list: (params?: any) => client.get('/quote/drafts', { params }),
  get: (id: number) => client.get(`/quote/drafts/${id}`),
  save: (data: any) => client.post('/quote/drafts', data),
  delete: (id: number) => (client as any).delete(`/quote/drafts/${id}`),
};

// 日志
export const logApi = {
  list: (params?: any) => client.get('/system/logs', { params }),
  create: (data: any) => client.post('/system/logs', data),
};

// 公告
export const announcementApi = {
  list: () => client.get('/system/announcements'),
  create: (data: any) => client.post('/system/announcements', data),
};

// 规档面料价格档位
export const fabricTierRangeApi = {
  list: (params?: any) => client.get('/fabric-tier-ranges', { params }),
  create: (data: any) => client.post('/fabric-tier-ranges', data),
  update: (id: number, data: any) => client.put(`/fabric-tier-ranges/${id}`, data),
  delete: (id: number) => (client as any).delete(`/fabric-tier-ranges/${id}`),
};

// 样本册管理
export const sampleBookApi = {
  list: (params?: any) => client.get('/sample-books', { params }),
  create: (data: any) => client.post('/sample-books', data),
  update: (id: number, data: any) => client.put(`/sample-books/${id}`, data),
  delete: (id: number) => (client as any).delete(`/sample-books/${id}`),
  // 样本册 PDF：下载（无则即时生成）/ 重新生成 / 批量导出 ZIP
  // 生成一份 PDF 需读取整册面料图片并合成，单独放宽超时到 10 分钟
  downloadPdf: (id: number, refresh = false) =>
    client.get(`/sample-books/${id}/pdf`, { params: refresh ? { refresh: 1 } : {}, responseType: 'blob', timeout: 600000 }),
  generatePdf: (id: number) => client.post(`/sample-books/${id}/generate-pdf`, {}, { timeout: 600000 }),
  exportPdfZip: (ids: number[], refresh = false) =>
    client.post('/sample-books/export-pdf', { ids, refresh }, { responseType: 'blob', timeout: 1800000 }),
};

// 面料品牌排序管理
export const fabricBrandApi = {
  list: (params?: any) => client.get('/fabric-brands', { params }),
  updateSort: (items: { brand: string; sort_order: number }[]) => client.put('/fabric-brands', { items }),
  update: (items: any[]) => client.put('/fabric-brands', { items }),
};

// 折扣审批
export const discountApprovalApi = {
  list: (params?: any) => client.get('/discount-approvals', { params }),
  get: (id: number) => client.get(`/discount-approvals/${id}`),
  create: (data: any) => client.post('/discount-approvals', data),
  update: (id: number, data: any) => client.put(`/discount-approvals/${id}`, data),
  delete: (id: number) => (client as any).delete(`/discount-approvals/${id}`),
};

// 面料库存（报价模块）
export const fabricInventoryApi = {
  list: (params?: any) => client.get('/fabric-inventory', { params }),
  bySampleBook: (params?: any) => client.get('/fabric-inventory/by-sample-book', { params }),
  get: (id: number) => client.get(`/fabric-inventory/${id}`),
  create: (data: any) => client.post('/fabric-inventory', data),
  update: (id: number, data: any) => client.put(`/fabric-inventory/${id}`, data),
  delete: (id: number) => (client as any).delete(`/fabric-inventory/${id}`),
  recalculateTiers: () => client.post('/fabric-inventory/recalculate-tiers'),
  uploadImage: (data: FormData) => client.post('/fabric-inventory/upload-image', data),
};

export const fabricTagApi = {
  list: () => client.get('/fabric-tags'),
  create: (data: any) => client.post('/fabric-tags', data),
  update: (id: number, data: any) => client.put(`/fabric-tags/${id}`, data),
  delete: (id: number) => (client as any).delete(`/fabric-tags/${id}`),
};

// 提成规则管理
export const commissionRuleApi = {
  list: () => client.get('/commission-rules'),
  create: (data: any) => client.post('/commission-rules', data),
  update: (id: number, data: any) => client.put(`/commission-rules/${id}`, data),
  delete: (id: number) => (client as any).delete(`/commission-rules/${id}`),
};

// 提成配置（全局参数 + 阶梯）
export const commissionConfigApi = {
  get: () => client.get('/commission-config'),
  update: (data: any) => client.put('/commission-config', data),
  reset: () => client.post('/commission-config/reset'),
};

// 处罚规则管理
export const penaltyRuleApi = {
  list: () => client.get('/penalty-rules'),
  create: (data: any) => client.post('/penalty-rules', data),
  update: (id: number, data: any) => client.put(`/penalty-rules/${id}`, data),
  delete: (id: number) => (client as any).delete(`/penalty-rules/${id}`),
};

// 处罚记录管理
export const penaltyRecordApi = {
  list: (params?: any) => client.get('/penalty-records', { params }),
  create: (data: any) => client.post('/penalty-records', data),
  delete: (id: number) => (client as any).delete(`/penalty-records/${id}`),
};

// 设计类型管理
export const designApi = {
  categories: () => client.get('/design-categories'),
  items: (params?: any) => client.get('/design-items', { params }),
  createCategory: (data: any) => client.post('/design-categories', data),
  updateCategory: (id: number, data: any) => client.put(`/design-categories/${id}`, data),
  deleteCategory: (id: number) => (client as any).delete(`/design-categories/${id}`),
  createOption: (data: any) => client.post('/design-options', data),
  updateOption: (id: number, data: any) => client.put(`/design-options/${id}`, data),
  deleteOption: (id: number) => (client as any).delete(`/design-options/${id}`),
  createValue: (data: any) => client.post('/design-option-values', data),
  updateValue: (id: number, data: any) => client.put(`/design-option-values/${id}`, data),
  deleteValue: (id: number) => (client as any).delete(`/design-option-values/${id}`),
};

// 成衣商城管理
export const readyWearApi = {
  categories: () => client.get('/ready-wear-categories'),
  createCategory: (data: any) => client.post('/ready-wear-categories', data),
  updateCategory: (id: number, data: any) => client.put(`/ready-wear-categories/${id}`, data),
  deleteCategory: (id: number) => (client as any).delete(`/ready-wear-categories/${id}`),
  products: (params?: any) => client.get('/ready-wear-products', { params }),
  productDetail: (id: number) => client.get(`/ready-wear-products/${id}`),
  createProduct: (data: any) => client.post('/ready-wear-products', data),
  updateProduct: (id: number, data: any) => client.put(`/ready-wear-products/${id}`, data),
  deleteProduct: (id: number) => (client as any).delete(`/ready-wear-products/${id}`),
};

// 基础订单配置
export const baseConfigApi = {
  baseOrderConfigs: (params?: any) => client.get('/base-order-configs', { params }),
  createBaseOrderConfig: (data: any) => client.post('/base-order-configs', data),
  updateBaseOrderConfig: (id: number, data: any) => client.put(`/base-order-configs/${id}`, data),
  deleteBaseOrderConfig: (id: number) => (client as any).delete(`/base-order-configs/${id}`),
  baseOrderConfigOptions: (params?: any) => client.get('/base-order-config-options', { params }),
  createBaseOrderConfigOption: (data: any) => client.post('/base-order-config-options', data),
  updateBaseOrderConfigOption: (id: number, data: any) => client.put(`/base-order-config-options/${id}`, data),
  deleteBaseOrderConfigOption: (id: number) => (client as any).delete(`/base-order-config-options/${id}`),
  bodyFeatureCategories: (params?: any) => client.get('/body-feature-categories', { params }),
  createBodyFeatureCategory: (data: any) => client.post('/body-feature-categories', data),
  updateBodyFeatureCategory: (id: number, data: any) => client.put(`/body-feature-categories/${id}`, data),
  deleteBodyFeatureCategory: (id: number) => (client as any).delete(`/body-feature-categories/${id}`),
  bodyFeatureItems: (params?: any) => client.get('/body-feature-items', { params }),
  createBodyFeatureItem: (data: any) => client.post('/body-feature-items', data),
  updateBodyFeatureItem: (id: number, data: any) => client.put(`/body-feature-items/${id}`, data),
  deleteBodyFeatureItem: (id: number) => (client as any).delete(`/body-feature-items/${id}`),
  embroideryConfig: (params?: any) => client.get('/embroidery-configs', { params }),
  embroideryBatchConfig: (params?: any) => client.get('/embroidery-configs/batch', { params }),
  updateEmbroideryConfig: (data: any) => client.put('/embroidery-configs', data),
  embroideryTypes: (params?: any) => client.get('/embroidery-types', { params }),
  createEmbroideryType: (data: any) => client.post('/embroidery-types', data),
  embroideryDesigns: (params?: any) => client.get('/embroidery-designs', { params }),
  createEmbroideryDesign: (data: any) => client.post('/embroidery-designs', data),
  updateEmbroideryDesign: (id: number, data: any) => client.put(`/embroidery-designs/${id}`, data),
  deleteEmbroideryDesign: (id: number) => (client as any).delete(`/embroidery-designs/${id}`),
  updateEmbroideryType: (id: number, data: any) => client.put(`/embroidery-types/${id}`, data),
  deleteEmbroideryType: (id: number) => (client as any).delete(`/embroidery-types/${id}`),
  embroideryFonts: (params?: any) => client.get('/embroidery-fonts', { params }),
  createEmbroideryFont: (data: any) => client.post('/embroidery-fonts', data),
  updateEmbroideryFont: (id: number, data: any) => client.put(`/embroidery-fonts/${id}`, data),
  deleteEmbroideryFont: (id: number) => (client as any).delete(`/embroidery-fonts/${id}`),
  embroideryPositions: (params?: any) => client.get('/embroidery-positions', { params }),
  createEmbroideryPosition: (data: any) => client.post('/embroidery-positions', data),
  updateEmbroideryPosition: (id: number, data: any) => client.put(`/embroidery-positions/${id}`, data),
  deleteEmbroideryPosition: (id: number) => (client as any).delete(`/embroidery-positions/${id}`),
  embroideryHeights: (params?: any) => client.get('/embroidery-heights', { params }),
  createEmbroideryHeight: (data: any) => client.post('/embroidery-heights', data),
  updateEmbroideryHeight: (id: number, data: any) => client.put(`/embroidery-heights/${id}`, data),
  deleteEmbroideryHeight: (id: number) => (client as any).delete(`/embroidery-heights/${id}`),
  embroideryColors: (params?: any) => client.get('/embroidery-colors', { params }),
  createEmbroideryColor: (data: any) => client.post('/embroidery-colors', data),
  updateEmbroideryColor: (id: number, data: any) => client.put(`/embroidery-colors/${id}`, data),
  deleteEmbroideryColor: (id: number) => (client as any).delete(`/embroidery-colors/${id}`),
  syncEmbroideryColorsToThreadColor: () => client.post('/sync-embroidery-colors-to-thread-color'),
  designSizeConfigs: (params?: any) => client.get('/design-size-configs', { params }),
  createDesignSizeConfig: (data: any) => client.post('/design-size-configs', data),
  updateDesignSizeConfig: (id: number, data: any) => client.put(`/design-size-configs/${id}`, data),
  deleteDesignSizeConfig: (id: number) => (client as any).delete(`/design-size-configs/${id}`),
};

// 审核备注
export const approvalRemarkApi = {
  list: (orderId: number) => client.get(`/orders/${orderId}/approval-remarks`),
  create: (orderId: number, data: any) => client.post(`/orders/${orderId}/approval-remarks`, data),
};

// 门店备忘录
export const storeMemoApi = {
  categories: () => client.get('/stores/memo-categories'),
  list: (storeId: number) => client.get(`/stores/${storeId}/memos`),
  create: (storeId: number, data: any) => client.post(`/stores/${storeId}/memos`, data),
  delete: (storeId: number, memoId: number) => (client as any).delete(`/stores/${storeId}/memos?memo_id=${memoId}`),
};

// B端客户注册审核
export const customerRegistrationAuditApi = {
  list: (params?: any) => client.get('/customer-registration-audit', { params }),
  update: (id: number, data: any) => client.put(`/customer-registration-audit/${id}`, data),
};

// 通用图片上传
export const commonApi = {
  uploadImage: (data: FormData) => client.post('/upload/image', data),
};

// 尺码表版型 & 加放规则
export const sizePatternApi = {
  list: (params?: any) => client.get('/size-patterns', { params }),
  detail: (id: number) => client.get(`/size-patterns/${id}`),
  create: (data: any) => client.post('/size-patterns', data),
  update: (id: number, data: any) => client.put(`/size-patterns/${id}`, data),
  delete: (id: number) => (client as any).delete(`/size-patterns/${id}`),
  rows: (id: number, params?: any) => client.get(`/size-patterns/${id}/rows`, { params }),
  createRow: (id: number, data: any) => client.post(`/size-patterns/${id}/rows`, data),
  updateRow: (id: number, rid: number, data: any) => client.put(`/size-patterns/${id}/rows/${rid}`, data),
  deleteRow: (id: number, rid: number) => (client as any).delete(`/size-patterns/${id}/rows/${rid}`),
  uploadImage: (id: number, data: FormData) => client.post(`/size-patterns/${id}/upload-image`, data),
};

export const easeApi = {
  preview: (data: { category: string; net: Record<string, any>; params?: Record<string, any> }) =>
    client.post('/ease/preview', data),
};
